import { Link as RouterLink } from "@tanstack/react-router";
import type { AnchorHTMLAttributes, ImgHTMLAttributes } from "react";

export type StartImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  alt: string;
  height?: number | `${number}`;
  src: string;
  width?: number | `${number}`;
};

export type StartLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "preload"> & {
  href: string;
  preload?: false | "intent" | "render" | "viewport";
  preloadDelay?: number;
};

const INTERNAL_LINK_ORIGIN = "https://start.local";

const isExternalHref = (href: string) => {
  if (!href || href.startsWith("#") || href.startsWith("?")) {
    return true;
  }

  if (href.startsWith("//")) {
    return true;
  }

  return /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(href);
};

const searchParamsToObject = (searchParams: URLSearchParams) => {
  const search: Record<string, string | string[]> = {};

  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key);
    if (values.length === 1) {
      search[key] = values[0] ?? "";
    } else if (values.length > 1) {
      search[key] = values;
    }
  }

  return Object.keys(search).length ? search : undefined;
};

const parseInternalHref = (href: string) => {
  const url = new URL(href, INTERNAL_LINK_ORIGIN);
  const pathname = url.pathname || "/";
  const search = searchParamsToObject(url.searchParams);
  const hash = url.hash ? url.hash.slice(1) : undefined;

  if (pathname === "/") {
    return {
      hash,
      search,
      to: "/" as const,
    };
  }

  return {
    hash,
    params: {
      _splat: decodeURIComponent(pathname.replace(/^\/+|\/+$/g, "")),
    },
    search,
    to: "/$" as const,
  };
};

export function StartImage({ alt, className, height, src, width, ...props }: StartImageProps) {
  return (
    <img
      {...props}
      alt={alt}
      className={className}
      height={height ? Number(height) : undefined}
      src={src}
      width={width ? Number(width) : undefined}
    />
  );
}

export function StartLink({
  children,
  href,
  preload = "intent",
  preloadDelay,
  rel,
  target,
  ...props
}: StartLinkProps) {
  const safeRel = target === "_blank" && rel === undefined ? "noopener noreferrer" : rel;
  const shouldUseBrowserNavigation =
    props.download || (target && target !== "_self") || isExternalHref(href);

  if (!shouldUseBrowserNavigation) {
    const link = parseInternalHref(href);

    return (
      <RouterLink
        {...(props as any)}
        activeOptions={{ exact: true, includeSearch: false }}
        hash={link.hash}
        params={("params" in link ? link.params : undefined) as never}
        preload={preload}
        preloadDelay={preloadDelay}
        rel={safeRel}
        search={link.search as never}
        target={target}
        to={link.to as never}
      >
        {children}
      </RouterLink>
    );
  }

  return (
    <a {...props} href={href} rel={safeRel} target={target}>
      {children}
    </a>
  );
}
